import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Chart from "react-apexcharts";
import { getMilestoneTrend } from "../../api/process";

export default function MTAChart() {
    const [milestones, setMilestones] = useState([]);
    const [projectPeriod, setProjectPeriod] = useState({ start: 0, end: 0 });
    const [isChartExpanded, setIsChartExpanded] = useState(false);
    const { id: projectId } = useParams();

    useEffect(() => {
        getMilestoneTrend().then((res) => {
            setMilestones(res.milestones || []);
            setProjectPeriod({
                start: new Date(res.project.period_start).getTime(),
                end: new Date(res.project.period_finish).getTime(),
            });
        });
    }, [projectId]);

    if (!milestones.length) {
        return (
            <div className="mobile-page-root mb-16">
                <div className="mobile-page-root-inner space-y-3 py-8">
                    <div className="bg-white rounded-lg p-6 border text-center">
                        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                        <p className="text-sm text-gray-500">MTA 데이터 로딩 중...</p>
                    </div>
                </div>
            </div>
        );
    }

    // ✅ 각 milestone 시리즈 (점 제거, 대각선 이후 구간 제외)
    const milestoneSeries = milestones.map((ms) => {
        const points = ms.updates
            .map((u) => ({
                x: u.cutoff === 0 ? projectPeriod.start : new Date(u.cutoff).getTime(),
                y: new Date(u.forecast).getTime(),
            }))
            .sort((a, b) => a.x - b.x);

        const clipped = [];

        for (let i = 0; i < points.length; i++) {
            const curr = points[i];
            if (i === 0) {
                clipped.push(curr);
                continue;
            }

            const prev = points[i - 1];

            // ⚙️ 아직 대각선 위라면 그대로 추가
            if (curr.y >= curr.x) {
                clipped.push(curr);
                continue;
            }

            // ⚙️ 대각선(y=x)을 지난 경우 → 교차점 계산 후 끊기
            const t = (prev.x - prev.y) / ((curr.y - prev.y) - (curr.x - prev.x));
            const x_cross = prev.x + (curr.x - prev.x) * t;
            const y_cross = x_cross; // y=x이므로 동일
            clipped.push({ x: x_cross, y: y_cross });
            break; // ✅ 교차점 이후 데이터는 버림
        }

        return { name: ms.name, data: clipped };
    });

    // ✅ 대각선 (Completion Line, y = x)
    const completionLine = {
        name: "Completion Line",
        data: [
            { x: projectPeriod.start, y: projectPeriod.start },
            { x: projectPeriod.end, y: projectPeriod.end },
        ],
    };

    const palette = ["#3b82f6", "#22c55e", "#f97316", "#a855f7", "#06b6d4", "#0ea5e9"];
    const colors = [
        ...milestoneSeries.map((_, i) => palette[i % palette.length]),
        "#ef4444", // 마지막 대각선은 빨강
    ];

    // ✅ 공통 설정
    const options = {
        chart: {
            type: "line",
            animations: { enabled: false },
            zoom: { enabled: false },
            toolbar: { show: false },
        },
        markers: { size: 0 },
        grid: {
            padding: { left: 10, right: 10 },
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: true } },
        },
        stroke: {
            width: [
                ...milestoneSeries.map(() => 1),
                2,
            ],
            curve: "straight",
            dashArray: [
                ...milestoneSeries.map(() => 0),
                0,
            ],
        },
        colors: colors,
        legend: { show: false },
        xaxis: {
            type: "datetime",
            min: projectPeriod.start,
            max: projectPeriod.end,
            tickAmount: 10,
            title: { text: "Cut-off Date" },
            labels: {
                formatter: (val) => {
                    const d = new Date(val);
                    return `${String(d.getFullYear()).slice(2)}/${String(
                        d.getMonth() + 1
                    ).padStart(2, "0")}`;
                },
            },
        },
        yaxis: {
            type: "datetime",
            min: projectPeriod.start,
            max: projectPeriod.end,
            tickAmount: 10,
            title: { text: "Forecast Finish Date" },
            labels: {
                formatter: (val) => {
                    const d = new Date(val);
                    return `${String(d.getFullYear()).slice(2)}/${String(
                        d.getMonth() + 1
                    ).padStart(2, "0")}`;
                },
            },
        },
        tooltip: {
            enabled: true,
            x: { format: "yyyy-MM-dd" },
            y: {
                formatter: (val) =>
                    new Date(val).toLocaleDateString("ko-KR", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                    }),
            },
        },
    };

    return (
        <div className="w-full min-w-0 overflow-x-hidden flex justify-center mb-16 px-4 box-border">
            <div className="w-full max-w-md min-w-0 space-y-3">
                {/* 헤더 */}
                <div className="bg-white rounded-lg p-3 border min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                        <div className="min-w-0 flex-1">
                            <h2 className="text-lg font-bold text-gray-900 truncate">MTA 차트</h2>
                            <p className="text-xs text-gray-500 mt-0.5">{milestones.length}개 마일스톤</p>
                        </div>
                    </div>
                </div>

                {/* 차트 카드 */}
                <div className="bg-white rounded-lg border overflow-hidden min-w-0">
                    {/* 차트 헤더 */}
                    <div className="bg-white px-3 py-2 flex items-center justify-between border-b min-w-0">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-gray-900 truncate">Milestone Trend</div>
                                <div className="text-xs text-gray-500 truncate">추세 분석</div>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsChartExpanded(true)}
                            className="btn-basic flex-shrink-0"
                        >
                            확대
                        </button>
                    </div>

                    {/* 범례 */}
                    <div className="bg-gray-50 px-3 py-2 border-b">
                        <div className="flex items-center justify-center gap-3 flex-wrap">
                            {milestones.slice(0, 3).map((ms, idx) => (
                                <div key={idx} className="flex items-center gap-1.5">
                                    <div 
                                        className="w-3 h-0.5 flex-shrink-0" 
                                        style={{ backgroundColor: ["#3b82f6", "#22c55e", "#f97316"][idx] }}
                                    />
                                    <span className="text-[10px] font-medium text-gray-700 whitespace-nowrap truncate max-w-[80px]">
                                        {ms.name}
                                    </span>
                                </div>
                            ))}
                            <div className="flex items-center gap-1.5">
                                <div className="w-3 h-0.5 bg-red-500 flex-shrink-0" />
                                <span className="text-[10px] font-medium text-gray-700 whitespace-nowrap">C-Line</span>
                            </div>
                        </div>
                    </div>

                    {/* 차트 */}
                    <div className="bg-white p-2">
                        <div className="overflow-x-auto">
                            <div className="min-w-[280px]">
                                <Chart
                                    options={options}
                                    series={[...milestoneSeries, completionLine]}
                                    type="line"
                                    width="100%"
                                    height={280}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 하단 힌트 */}
                    <div className="bg-gray-50 px-3 py-2 text-[10px] text-gray-600 text-center border-t">
                        💡 마일스톤의 완료 예정일 추세를 확인할 수 있습니다
                    </div>
                </div>

                {/* 확대 모달 */}
                {isChartExpanded && (
                    <div 
                        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-2 sm:p-4 animate-fadeIn"
                        onClick={() => setIsChartExpanded(false)}
                    >
                        <div
                            className="w-full max-w-[98vw] sm:max-w-4xl md:max-w-5xl bg-white rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* 헤더 */}
                            <div className="bg-white px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between border-b">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm sm:text-base font-semibold text-gray-900">MTA 차트 확대</h3>
                                </div>
                                <button
                                    onClick={() => setIsChartExpanded(false)}
                                    className="btn-cancel flex-shrink-0"
                                >
                                    닫기
                                </button>
                            </div>

                            {/* 범례 */}
                            <div className="bg-gray-50 px-2 py-2 sm:px-4 sm:py-3 border-b">
                                <div className="flex items-center justify-center gap-4 flex-wrap">
                                    {milestones.map((ms, idx) => (
                                        <div key={idx} className="flex items-center gap-1.5">
                                            <div 
                                                className="w-4 h-1 flex-shrink-0" 
                                                style={{ backgroundColor: ["#3b82f6", "#22c55e", "#f97316", "#a855f7", "#06b6d4"][idx % 5] }}
                                            />
                                            <span className="text-[10px] sm:text-xs font-medium text-gray-700 whitespace-nowrap">
                                                {ms.name}
                                            </span>
                                        </div>
                                    ))}
                                    <div className="flex items-center gap-1.5">
                                        <div className="w-4 h-1 bg-red-500 flex-shrink-0" />
                                        <span className="text-[10px] sm:text-xs font-medium text-gray-700 whitespace-nowrap">C-Line</span>
                                    </div>
                                </div>
                            </div>

                            {/* 확대 차트 */}
                            <div className="bg-white p-2 sm:p-4">
                                <div className="flex items-center justify-center gap-2 mb-3 text-[10px] sm:text-xs text-gray-500">
                                    <span>← 좌우로 스크롤하여 전체 차트 확인 →</span>
                                </div>

                                <div className="overflow-x-auto scrollbar-thin">
                                    <div className="min-w-[700px] sm:min-w-[900px]">
                                        <Chart
                                            options={options}
                                            series={[...milestoneSeries, completionLine]}
                                            type="line"
                                            width="100%"
                                            height={window.innerWidth < 640 ? 400 : window.innerWidth < 768 ? 500 : 600}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* 하단 정보 */}
                            <div className="bg-gray-50 px-2 py-2 sm:px-4 sm:py-3 text-[10px] sm:text-xs text-gray-600 text-center border-t">
                                💡 마일스톤의 추세를 확인하여 프로젝트 진행 상황을 파악할 수 있습니다
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
